// Overlay layer (docs/09 §C2) — a fixed layer rendered ABOVE both playback
// buffers: a corner logo watermark and/or a persistent bottom ticker strip.
//
// The layer is a single DOM node appended after the two playback layers, so it
// stacks on top and never participates in the media crossfade. It reads its
// logo through the SAME injected MediaSource as content (offline-safe on the
// player, live-fetched in the CMS preview) and is applied ATOMICALLY with the
// manifest version swap: `apply()` is a no-op when the overlay is byte-for-byte
// unchanged, so an unrelated manifest change never flickers the logo/ticker.
// Zero runtime dependencies — hand-written like the rest of the engine.

import type { MediaSource } from './ports.ts';
import type { Overlay } from './types.ts';

/** True when the overlay has anything to render (a logo or a ticker). */
export function hasOverlayContent(overlay: Overlay | null | undefined): boolean {
  if (!overlay) return false;
  return overlay.logo !== null || overlay.ticker !== null;
}

/** The cacheable media URL of the overlay logo, or null. Used by the cache plan. */
export function overlayLogoUrl(overlay: Overlay | null | undefined): string | null {
  return overlay?.logo?.url ?? null;
}

/**
 * Marquee duration (seconds) to scroll one copy of the ticker at `speed_px_s`.
 * With two side-by-side copies translated by -50%, one copy width travels per
 * cycle. Guards against a zero/negative speed and clamps to a sane minimum so a
 * tiny string never scrolls implausibly fast.
 */
export function tickerDurationS(copyWidthPx: number, speedPxS: number): number {
  const speed = speedPxS > 0 ? speedPxS : 60;
  const width = copyWidthPx > 0 ? copyWidthPx : 0;
  return Math.max(4, width / speed);
}

/** Stable identity of an overlay so `apply()` can skip unchanged re-renders. */
export function overlayKey(overlay: Overlay | null | undefined): string | null {
  return hasOverlayContent(overlay) ? JSON.stringify(overlay) : null;
}

export class OverlayLayer {
  private root: HTMLElement;
  /** JSON of the currently-applied overlay; null when hidden. */
  private applied: string | null = null;
  /** Generation counter so a slow async logo load can't clobber a newer apply. */
  private gen = 0;

  constructor(parent: HTMLElement, private media: MediaSource) {
    this.root = document.createElement('div');
    this.root.className = 'overlay-layer';
    this.root.setAttribute('aria-hidden', 'true');
    this.root.style.display = 'none';
    parent.appendChild(this.root);
  }

  /**
   * Apply an overlay (or null to hide). No-op when unchanged — the logo/ticker
   * stay put through unrelated manifest swaps (no flicker). A changed overlay is
   * rebuilt atomically once its logo (if any) resolves from the MediaSource.
   */
  apply(overlay: Overlay | null | undefined): void {
    const key = overlayKey(overlay);
    if (key === this.applied) return;
    this.applied = key;
    const gen = ++this.gen;
    if (!key || !overlay) {
      this.root.replaceChildren();
      this.root.style.display = 'none';
      return;
    }
    void this.render(overlay, gen);
  }

  private async render(overlay: Overlay, gen: number): Promise<void> {
    // Ticker first in the DOM so a bottom-anchored logo (which follows it) is
    // lifted clear of the strip via the `.overlay-ticker ~ .overlay-pos-*` rule;
    // the logo still stacks above the ticker (later paint) so it is never hidden.
    const ticker = overlay.ticker
      ? this.buildTicker(overlay.ticker.text, overlay.ticker.speed_px_s)
      : null;

    let logo: HTMLElement | null = null;
    if (overlay.logo) {
      // Resolve through the MediaSource so the logo behaves exactly like content
      // (offline-safe on the player). A miss simply omits the logo (ticker still
      // shows).
      const src = await this.media.resolveSrc(overlay.logo.url);
      if (gen !== this.gen) return; // superseded by a newer apply()
      if (src) {
        const img = document.createElement('img');
        img.className = `overlay-logo overlay-pos-${overlay.logo.position}`;
        // Container units: sized against the stage box, so the logo is
        // proportional in a small preview and a fullscreen wall alike.
        img.style.width = `${overlay.logo.size_pct}cqw`;
        img.style.opacity = String(overlay.logo.opacity / 100);
        img.decoding = 'async';
        img.src = src;
        logo = img;
      }
    }

    if (gen !== this.gen) return;
    const children = [ticker, logo].filter((el): el is HTMLElement => el !== null);
    this.root.replaceChildren(...children);
    this.root.style.display = children.length ? 'block' : 'none';
  }

  private buildTicker(text: string, speedPxS: number): HTMLElement {
    const strip = document.createElement('div');
    strip.className = 'overlay-ticker';

    const track = document.createElement('div');
    track.className = 'overlay-ticker-track';

    // Two identical copies → the -50% keyframe produces a seamless wrap. The
    // second copy is decorative (the marquee just repeats the text).
    const primary = document.createElement('span');
    primary.className = 'overlay-ticker-text';
    primary.textContent = text;
    const clone = primary.cloneNode(true) as HTMLElement;
    clone.setAttribute('aria-hidden', 'true');
    track.append(primary, clone);
    strip.appendChild(track);

    // Duration depends on the rendered width, known only after layout. Measure
    // on the next frame and set it; prefers-reduced-motion disables the marquee
    // entirely in CSS, so this value is simply unused there.
    requestAnimationFrame(() => {
      const copyWidth = primary.getBoundingClientRect().width;
      track.style.animationDuration = `${tickerDurationS(copyWidth, speedPxS)}s`;
    });

    return strip;
  }
}
